import { formatMinorAmount } from '@repo/common';
import { type BillingHistoryEntry, BillingHistoryEntryKind, BillingHistoryStatus } from '@repo/frontend-shared';

import { formatBillingDate, formatProviderStatus } from './billing-format';

/**
 * What each state is called on screen.
 *
 * Keyed by the enum so a status added to the schema is caught here the moment
 * the client regenerates. Until it does, the server can still send a state
 * this build has never heard of, which is why the reader below falls back
 * rather than indexing straight into the record.
 */
const STATUS_LABELS: Record<BillingHistoryStatus, string> = {
  [BillingHistoryStatus.PENDING]: 'Pending',
  [BillingHistoryStatus.SUCCEEDED]: 'Paid',
  [BillingHistoryStatus.FAILED]: 'Failed',
  [BillingHistoryStatus.CANCELLED]: 'Cancelled',
  [BillingHistoryStatus.PARTIALLY_REFUNDED]: 'Partially refunded',
  [BillingHistoryStatus.REFUNDED]: 'Refunded',
  [BillingHistoryStatus.DISPUTED]: 'Disputed',
  [BillingHistoryStatus.SUPPORT_REQUIRED]: 'Contact support',
};

/**
 * The provider's invoice vocabulary, in English.
 *
 * `uncollectible` and `void` are accounting words for the provider's own
 * ledger. The field is a bare string in the schema, so an unmapped value is
 * title-cased rather than dropped: a status we have not seen still tells the
 * customer more than a blank does.
 */
const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  open: 'Awaiting payment',
  paid: 'Paid',
  uncollectible: 'Written off',
  void: 'Voided',
};

/**
 * The status label, for a status this build may not know.
 *
 * A client running against a newer schema receives the raw string; indexing
 * the record would render an empty cell on a row about someone's money, so an
 * unknown state reads as its own words instead.
 */
export function describeBillingHistoryStatus(status: BillingHistoryStatus): string {
  return Object.hasOwn(STATUS_LABELS, status) ? STATUS_LABELS[status] : formatProviderStatus(status);
}

export function describeInvoiceStatus(status: string): string {
  const normalized = status.trim().toLowerCase();
  return Object.hasOwn(INVOICE_STATUS_LABELS, normalized)
    ? INVOICE_STATUS_LABELS[normalized]
    : formatProviderStatus(normalized);
}

/**
 * The invoice line under a row's description.
 *
 * The number is the reference the customer would quote to support, so it
 * leads. Everything after it is present only when the provider recorded it: a
 * period of `undefined – undefined` reads as a system that lost the dates, and
 * an empty status would leave a dangling separator on the line support reads.
 */
export function describeBillingHistoryInvoice(invoice: BillingHistoryEntry['invoice']): string | undefined {
  if (!invoice) {
    return undefined;
  }

  const parts = [`Invoice ${invoice.number}`, describeInvoiceStatus(invoice.status)];

  if (invoice.periodStart && invoice.periodEnd) {
    parts.push(`${formatBillingDate(invoice.periodStart)} – ${formatBillingDate(invoice.periodEnd)}`);
  }

  // The sequence is not repeated here: the commitment line above the invoice
  // states it, and a row reading "Payment 2 / Invoice INV-42 · Paid ·
  // Installment 2" says the same thing twice in adjacent lines of one cell.
  return parts.filter(Boolean).join(' · ');
}

/**
 * What kind of commitment a row belongs to, as the second line under its
 * description.
 *
 * A recurring charge carries an invoice for the period it covers, so a
 * purchase without one is a single payment. The sequence is stated bare —
 * "Payment 2" rather than "Payment 2 of 6" — because the entry carries no
 * instalment total; inventing one would misstate what the customer still owes.
 */
export function describeBillingHistoryCommitment(entry: BillingHistoryEntry): string | undefined {
  const sequence = entry.invoice?.installmentSequence;
  if (sequence !== null && sequence !== undefined) {
    return `Payment ${sequence}`;
  }

  if (entry.kind === BillingHistoryEntryKind.PURCHASE && !entry.invoice) {
    return 'One-time purchase';
  }

  return undefined;
}

export interface BillingHistoryAmountLines {
  gross: string;
  refunded?: string;
  net?: string;
}

/**
 * The three figures a partially refunded row has to carry.
 *
 * `net` is omitted when it restates the gross, which is what happens both when
 * nothing came back and when the refund settled in another currency — the
 * server reports gross as the net in that case because the two amounts cannot
 * be subtracted from each other.
 */
export function buildBillingHistoryAmountLines(entry: BillingHistoryEntry): BillingHistoryAmountLines {
  const gross = formatMinorAmount(entry.gross.amountMinor, entry.gross.currency);

  if (!entry.refunded) {
    return { gross };
  }

  const restatesGross =
    entry.net.amountMinor === entry.gross.amountMinor && entry.net.currency === entry.gross.currency;

  return {
    gross,
    refunded: `Refunded ${formatMinorAmount(entry.refunded.amountMinor, entry.refunded.currency)}`,
    net: restatesGross ? undefined : `Net ${formatMinorAmount(entry.net.amountMinor, entry.net.currency)}`,
  };
}
